'use client'

import { faUser } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { type ComponentProps, useEffect, useRef, useState } from 'react'
import ProductCardSearch from '@/components/Catalog/ProductCardSearch'
import { useLogout } from '@/hooks/business/auth/useLogout'
import { useCart } from '@/hooks/business/cart/useCart'
import { useProductSearch } from '@/hooks/business/catalog/useProductSearch'
import { useCustomer } from '@/hooks/business/customer/useCustomer'
import { useIsMobile } from '@/hooks/ui/useIsMobile'
import type { IProductSearchResult } from '@/schemas/catalog/product'
import EditUserModal from '~/components/Customer/EditUserModal'
import Button from '~/components/Form/Button'

/** O card da busca (catálogo) ainda declara o próprio tipo de resultado; o do schema é o que a API devolve. */
const toSearchCard = (result: IProductSearchResult) =>
    result as unknown as ComponentProps<typeof ProductCardSearch>['data']

/**
 * Barra fixa da loja: logo, menu, busca com sugestões (`useProductSearch`), entrar ou o menu do cliente
 * (`useCustomer`, `useLogout`) e o carrinho com o contador (`useCart`, só a store: quem revalida o carrinho são as
 * telas do carrinho e do checkout).
 */
export const Navbar: React.FC = () => {
    const router = useRouter()
    const pathname = usePathname()
    const { logout } = useLogout()
    const { customer: user } = useCustomer()
    const { items: cartItems } = useCart({ enabled: false })
    const [showMenu, setShowMenu] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [showSearchMobile, setShowSearchMobile] = useState(false)
    const isMobile = useIsMobile()
    const [showSubMenu, setShowSubMenu] = useState(false)
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)

    const [searchTerm, setSearchTerm] = useState<string>('')
    const { results: searchResults, term: resultsTerm, isSearching } = useProductSearch(searchTerm)
    // Sugestões fechadas (clique fora ou num produto) até o texto mudar.
    const [dismissedTerm, setDismissedTerm] = useState<string | null>(null)
    const results = resultsTerm === dismissedTerm ? [] : searchResults
    const clearResults = () => setDismissedTerm(resultsTerm)

    const toggleSearch = () => {
        //set focust to search input
        const searchInput = document.getElementById('searchInputDesktop')
        if (searchInput) searchInput.focus()
        setShowSearch((p) => !p)
    }

    const toggleSearchMobile = () => setShowSearchMobile((p) => !p)

    // O menu fecha a cada navegação (pathname), ajustado na renderização em vez de num efeito.
    const [menuPathname, setMenuPathname] = useState(pathname)
    if (menuPathname !== pathname) {
        setMenuPathname(pathname)
        setShowMenu(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            redirectToSearchPage()
        }
    }

    // A busca completa leva o texto digitado (antes lia um estado que nada preenchia, e o Enter não fazia nada).
    const redirectToSearchPage = () => {
        const keyword = searchTerm.trim()
        if (!keyword) return
        clearResults()
        setSearchTerm('')
        router.push(`/produtos?keyword=${encodeURIComponent(keyword)}`)
    }

    const handleLogout = () => {
        logout()
    }

    const firstName = () => {
        if (user?.name) {
            const nameParts = user.name.split(' ')
            return nameParts[0]
        }
        return ''
    }

    const handleSubMenuClick = () => {
        if (!user?.name) {
            setIsEditUserModalOpen(true)
        } else {
            setShowSubMenu(!showSubMenu)
        }
    }

    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // Tipagem do evento como MouseEvent para acesso à propriedade target
            const target = event.target as HTMLElement // Assegura que target será tratado como HTMLElement
            if (wrapperRef.current && !wrapperRef.current.contains(target)) {
                setDismissedTerm(resultsTerm)
                if (showSearch) setShowSearch(false)
                if (showSearchMobile) setShowSearchMobile(false)
                if (showMenu) setShowMenu(false)
                if (showSubMenu) setShowSubMenu(false)
            }
        }

        // Adiciona o ouvinte de eventos ao documento
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            // Remove o ouvinte de eventos ao limpar
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showSearch, showSearchMobile, showMenu, showSubMenu, resultsTerm])

    return (
        <>
            <EditUserModal setIsEditUserModalOpen={setIsEditUserModalOpen} open={isEditUserModalOpen} />
            <div className="container 2xl:max-w-[1500px] max-w-[1200px] fixed top-0 mx-auto border-0 z-60 md:py-0 md:left-0 md:right-0 w-screen">
                <nav
                    className="flex items-center sm:justify-between sm:px-6 flex-wrap rounded-b-2xl border-0 z-60 py-0 sticky"
                    ref={wrapperRef}
                >
                    {results?.length > 0 && (
                        <div className="absolute right-0 z-50 w-6/12 p-2 pb-5 mt-1 max-md:mt-16 overflow-auto bg-white/90 shadow-md max-md:w-full rounded-xl top-full max-h-[80vh]">
                            {isSearching ? (
                                <div className="text-lg font-light loader text-secondary">Carregando...</div>
                            ) : (
                                results.map((result) => (
                                    <ProductCardSearch
                                        key={result.id}
                                        data={toSearchCard(result)}
                                        clear={clearResults}
                                    />
                                ))
                            )}
                        </div>
                    )}

                    <div className="navbar-left">
                        <div className="relative flex border-0 z-60 w-full">
                            <div className="flex z-60 w-full h-[80px] bg-primary items-center justify-between px-5 rounded-b-2xl">
                                <div className="flex items-center justify-start">
                                    <Link href="/">
                                        {showMenu && isMobile ? (
                                            <Image
                                                src="/assets/images/navbar/Logo_Simbol_Glofi.svg"
                                                height={42}
                                                width={40}
                                                alt="Glofi"
                                                className="ml-2 cursor-pointer"
                                                id="logo"
                                            />
                                        ) : (
                                            <Image
                                                src="/assets/images/logo-glofi.svg"
                                                height={36}
                                                width={83}
                                                alt="Glofi"
                                                className="ml-2 cursor-pointer"
                                                id="logo"
                                            />
                                        )}
                                    </Link>
                                    {showMenu ? null : (
                                        <Image
                                            src="/assets/images/navbar/menu.svg"
                                            height={17}
                                            width={20}
                                            alt="menu"
                                            className="ml-24 mr-3 cursor-pointer max-md:ml-3"
                                            onClick={() => setShowMenu(true)}
                                        />
                                    )}
                                </div>

                                <div className="flex flex-row items-center justify-end">
                                    {showMenu ? (
                                        <div className="flex items-center mb-login">
                                            {isMobile && (
                                                <>
                                                    <Image
                                                        src="/assets/icons/lupa.svg"
                                                        height={30}
                                                        width={30}
                                                        alt="Search icon"
                                                        className="mr-3 cursor-pointer"
                                                        onClick={toggleSearchMobile}
                                                        id="searchBtn"
                                                    />
                                                    <div>
                                                        {user ? (
                                                            <div>
                                                                <button type="button" onClick={handleSubMenuClick}>
                                                                    <FontAwesomeIcon
                                                                        icon={faUser}
                                                                        color="#FFFFFF"
                                                                        size="xl"
                                                                        id="loginBtn"
                                                                        className="cursor-pointer"
                                                                    />
                                                                </button>

                                                                {showSubMenu && (
                                                                    <div
                                                                        className={`
                                    flex bg-submenu-gray px-5 rounded-2xl min-h-[80px] absolute
                                    top-8 right-6 border-0 w-56 z-40 py-4 mt-2
                                    ${
                                        showMenu ? (isMobile ? 'translate-y-[36%]' : '') : ''
                                    } transition-transform duration-200`}
                                                                    >
                                                                        <div className="flex flex-col w-full gap-2">
                                                                            <Link
                                                                                href="/perfil"
                                                                                className="py-2 text-center text-gray-600 rounded-md text-md hover:bg-secondary hover:text-bg font-sora"
                                                                                onClick={() =>
                                                                                    setShowSubMenu(!showSubMenu)
                                                                                }
                                                                            >
                                                                                Olá, {firstName()}
                                                                            </Link>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    handleLogout()
                                                                                    setShowSubMenu(!showSubMenu)
                                                                                }}
                                                                                className="py-2 text-gray-600 rounded-md text-md hover:bg-secondary hover:text-bg font-sora"
                                                                            >
                                                                                Sair
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <Link href="/login">
                                                                <Button size="sm" color="white">
                                                                    entrar
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                            <Image
                                                src="/assets/images/navbar/x.svg"
                                                height={20}
                                                width={20}
                                                alt="menu"
                                                className="ml-24 mr-2 cursor-pointer max-md:ml-3"
                                                onClick={() => {
                                                    setShowMenu(false)
                                                    setShowSearchMobile(false)
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-login side-cart-div">
                                            <div className="relative inline-flex w-max side-cart-div mt-2 [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
                                                <Link href="/carrinho">
                                                    <Image
                                                        src="/assets/icons/cart.svg"
                                                        height={24}
                                                        width={27}
                                                        alt="Carrinho"
                                                        className="hidden cursor-pointer max-md:block"
                                                        id="cartBtn"
                                                        // onClick={toggleCart}
                                                    />
                                                </Link>
                                            </div>
                                            <div className="md:hidden">
                                                <CartCount count={cartItems.length} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div
                                className={`search-menu ${
                                    showSearchMobile ? 'translate-y-[85%]  w-full rounded-t-lg' : ''
                                } transition-transform duration-200`}
                                id="search-menu"
                            >
                                <input
                                    autoComplete="off"
                                    type="search"
                                    placeholder="digite sua busca"
                                    id="searchInputDesktop"
                                    className="w-full text-sm text-center bg-transparent border-b border-calm-gray-ultralight focus:border-strong-blue hover:border-strong-blue-light x-2 focus:outline-hidden text-harmonic-blue "
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div
                                className={`
                flex bg-submenu-gray px-5 rounded-2xl md:rounded-t-none h-[80px] absolute top-0 left-0 border-0 w-full z-40 py-4 items-center justify-center max-sm:h-[80px]
                ${
                    showMenu && !showSearchMobile ? (isMobile ? 'translate-y-[85%]' : 'translate-x-[85%]') : ''
                } transition-transform duration-200`}
                            >
                                <div className={`flex-col gap-4 ${showMenu ? 'flex' : 'hidden'}`}>
                                    <Link href="/produtos" className="pl-4 text-sm font-bold text-gray-600">
                                        todos os produtos
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="navbar-right">
                        <div className="flex items-center">
                            <Image
                                src="/assets/icons/lupa.svg"
                                height={22}
                                width={24}
                                alt="Search icon"
                                className="mr-6 cursor-pointer"
                                onClick={toggleSearch}
                                id="searchBtn"
                            />
                            <div className="relative">
                                {user ? (
                                    <div>
                                        <button type="button" onClick={handleSubMenuClick}>
                                            <FontAwesomeIcon
                                                icon={faUser}
                                                color="#FFFFFF"
                                                size="xl"
                                                id="loginBtn"
                                                className="cursor-pointer"
                                            />
                                        </button>

                                        {showSubMenu && (
                                            <div
                                                className={`
                          flex bg-submenu-gray px-5 rounded-2xl min-h-[60px] absolute
                          top-0 right-0 border-0 w-56 z-40 py-4 mt-8
                          ${showMenu ? (isMobile ? 'translate-y-[36%]' : '') : ''} transition-transform duration-200`}
                                            >
                                                <div className="flex flex-col w-full gap-2">
                                                    <Link
                                                        href="/perfil"
                                                        className="py-2 text-center text-gray-600 rounded-md text-md hover:bg-secondary hover:text-bg font-sora"
                                                        onClick={() => setShowSubMenu(!showSubMenu)}
                                                    >
                                                        Olá, {firstName()}
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleLogout()
                                                            setShowSubMenu(!showSubMenu)
                                                        }}
                                                        className="py-2 text-gray-600 rounded-md text-md hover:bg-secondary hover:text-bg font-sora"
                                                    >
                                                        Sair
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link href="/login">
                                        <Button size="sm" color="white">
                                            entrar
                                        </Button>
                                    </Link>
                                )}
                            </div>
                            <div className="relative ml-2 xl:top-1">
                                <div className="relative inline-flex w-max side-cart-div [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
                                    <Link href="/carrinho">
                                        <Image
                                            src="/assets/icons/cart.svg"
                                            height={24}
                                            width={27}
                                            alt="Cart icon"
                                            className="cursor-pointer"
                                            // onClick={toggleCart}
                                        />
                                    </Link>
                                </div>
                                <div>
                                    <CartCount count={cartItems.length} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* max-md:hidden com "!": no Tailwind 3 a variante vinha depois do .search-menu (display: flex) e escondia a
                        busca no celular; no 4 o .search-menu fica depois. */}
                    <div
                        className={`search-menu max-md:hidden! ${
                            showSearch ? 'translate-x-[-95%] top-0 ' : ''
                        }transition-transform duration-200 w-56`}
                        id="search-menu"
                    >
                        <input
                            autoComplete="off"
                            type="search"
                            placeholder="digite sua busca"
                            id="searchInputDesktop"
                            className="bg-transparent border-b text-sm border-calm-gray-ultralight focus:border-strong-blue hover:border-strong-blue-light  pl-3 focus:outline-hidden text-harmonic-blue w-[80%]"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                </nav>
            </div>
        </>
    )
}

/** Contador de itens (linhas) do carrinho sobre o ícone; carrinho vazio não mostra nada. */
const CartCount: React.FC<{ count: number }> = ({ count }) =>
    count > 0 ? (
        <span className="text-white text-sm bg-red w-5 h-5 absolute max-md:top-4 max-md:right-3 -top-2 -right-3 rounded-full flex items-center justify-center p-2">
            {count}
        </span>
    ) : null
